import { useEffect, useState } from 'react';

import { COLORS } from '@theme/colors';
import { Direction, DistanceProps } from '@definitions/common.types';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { MOBILE } from '@theme/media';

export enum SpaceViewStep {
  First = 1,
  Second,
  Third,
}

export const CANVAS_WIDTH = 676;
export const CANVAS_MARGIN = 68;
export const CANVAS_MOBILE_MARGIN = 50;
const defaultDistance = { value: 0, direction: Direction.HORIZONTAL };

export const useArtworkSpaceView = () => {
  const [isSpaceViewMode, setIsSpaceViewMode] = useState(false);
  const { breakpoint } = useBreakpoint();
  const [backgroundImage, setBackgroundImage] = useState('');
  const [measure, setMeasure] = useState<number>(0);
  const [spaceViewStep, setSpaceViewStep] = useState<SpaceViewStep>(SpaceViewStep.First);
  const [distance, setDistance] = useState<DistanceProps>(defaultDistance);
  const toggleSpaceViewMode = () => setIsSpaceViewMode((prevState) => !prevState);
  const closeSpaceViewMode = () => setIsSpaceViewMode(false);
  const openSpaceViewMode = () => {
    if (breakpoint !== MOBILE) {
      window.scrollTo(0, 0);
    }
    setIsSpaceViewMode(true);
  };

  const clearAllStates = () => {
    if (!isSpaceViewMode) {
      setSpaceViewStep(SpaceViewStep.First);
      setBackgroundImage('');
      setMeasure(0);
      setDistance(defaultDistance);
    }
  };

  useEffect(() => {
    clearAllStates();
  }, [clearAllStates]);

  useEffect(() => {
    if (backgroundImage !== '') {
      return setSpaceViewStep(SpaceViewStep.Second);
    }
  }, [setSpaceViewStep, backgroundImage]);

  return {
    isSpaceViewMode,
    toggleSpaceViewMode,
    backgroundImage,
    setBackgroundImage,
    measure,
    setMeasure,
    spaceViewStep,
    setSpaceViewStep,
    closeSpaceViewMode,
    openSpaceViewMode,
    distance,
    setDistance,
  };
};

export const useArtworkSpaceViewCanvas = (
  canvas: HTMLCanvasElement | null,
  setDistance: (data: DistanceProps) => void,
  spaceViewStep: number
) => {
  let clicksCount = 0;
  const lastCoordinates: Array<number> = [];

  const draw = (event: MouseEvent) => {
    if (canvas) {
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      setDistance({ value: 0, direction: Direction.HORIZONTAL });
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (clicksCount === 0) {
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, 2 * Math.PI, true);
        ctx.fillStyle = COLORS.BLACK;
        ctx.fill();
        clicksCount++;
        lastCoordinates[0] = x;
        lastCoordinates[1] = y;
        return;
      }

      const headLength = 10;
      const angleEndArrow = 4;
      const [fromX, fromY] = lastCoordinates;
      const angle = Math.atan2(y - fromY, x - fromX);
      const angle2 = Math.atan2(fromY - y, fromX - x);

      ctx.beginPath();
      ctx.setLineDash([2, 2]);
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(x, y);
      ctx.lineWidth = 2;
      ctx.strokeStyle = COLORS.BLACK;
      ctx.stroke();

      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.moveTo(x, y);
      ctx.lineTo(
        x - headLength * Math.cos(angle - Math.PI / angleEndArrow),
        y - headLength * Math.sin(angle - Math.PI / angleEndArrow)
      );
      ctx.moveTo(x, y);
      ctx.lineTo(
        x - headLength * Math.cos(angle + Math.PI / angleEndArrow),
        y - headLength * Math.sin(angle + Math.PI / angleEndArrow)
      );
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(
        fromX - headLength * Math.cos(angle2 - Math.PI / angleEndArrow),
        fromY - headLength * Math.sin(angle2 - Math.PI / angleEndArrow)
      );
      ctx.moveTo(fromX, fromY);
      ctx.lineTo(
        fromX - headLength * Math.cos(angle2 + Math.PI / angleEndArrow),
        fromY - headLength * Math.sin(angle2 + Math.PI / angleEndArrow)
      );
      ctx.strokeStyle = COLORS.BLACK;
      ctx.stroke();

      const absX = Math.abs(x - lastCoordinates[0]);
      const absY = Math.abs(y - lastCoordinates[1]);
      const value = Math.sqrt(Math.pow(absX, 2) + Math.pow(absY, 2));

      setDistance({ value, direction: absX < absY ? Direction.VERTICAL : Direction.HORIZONTAL });
      clicksCount = 0;
    }
  };

  useEffect(() => {
    if (canvas && spaceViewStep === SpaceViewStep.Second) {
      canvas.addEventListener('click', draw);

      return () => canvas.removeEventListener('click', draw);
    }
  }, [canvas, spaceViewStep]);
};
